export const workExperience = {
  brand: 'OVERHEAR',
  title: 'First instincts',
  subtitle: 'A worksheet for work experience. Onboarding and a first use of the app.',
  intro:
    'Write what you actually thought. There are no wrong answers, and we are not marking you on whether you got it right. We want to see where the app is clear, where it is confusing, and where it seems to lead you.',
  rule: 'Do not ask for help until you have written your first instinct. If you get stuck, stay with it for a minute, write what you tried, and only then ask.',
  practical:
    'About an hour. This page is the worksheet. OVERHEAR itself is the app on the phone. You need that app installed, location available, and somewhere you can walk a short distance. Do this on your own. Do not watch someone else go first.',
  savedHint: 'Answers stay on this phone or computer. Copy or download them before you leave.',
  placeholder: 'Write here',
  savedCount: 'Saved on this device · {{count}} written',
  copy: 'Copy answers',
  download: 'Download',
  print: 'Print',
  clear: 'Clear answers',
  clearConfirm: 'Clear everything you have written on this device?',
  copied: 'Answers copied',
  copyFailed: 'Could not copy answers',
  downloaded: 'Answers downloaded',
  before: {
    title: 'Before you open the app',
    name: 'Your name',
    date: 'Date',
    phone: 'Phone (iPhone or Android)',
    usedBefore: 'Have you used OVERHEAR before?',
    guess: 'In one line, what do you think an app called OVERHEAR might be for?',
  },
  firstScreen: {
    title: '1. The video',
    instruction:
      'Open the app. The first thing you see is a video. Watch it. Do not skip it unless that is what you genuinely want to do.',
    purpose: 'What do you think this app is for, from the video alone?',
    action: 'What is the first thing you feel you should do?',
    eye: 'What is your eye drawn to? Name the words, pictures or buttons.',
    watched: 'Did you watch it through, or try to skip it? Why?',
    sentence: 'In one sentence, what do you now think you are supposed to do in this app?',
    words: 'Write down any words that stuck with you.',
    unclear: 'Write down anything you did not understand. If a word was new, say what you guessed it meant.',
    confusing: 'Is anything already confusing? What, and why?',
    expect: 'What do you think will happen when the video ends?',
    leading: 'Where did the video seem to be leading you?',
    next: 'When it finished, what did you expect the next screen to be? What did you actually get?',
  },
  gettingIn: {
    title: '2. Choosing how to sign on',
    instruction:
      'When the video ends, you land on a screen where you choose how to sign on. Answer the questions below before you choose.',
    optionsLead: 'List every way to sign on that you can see, in the order they appear.',
    optionColumn: 'What the option says',
    pick: 'Which one would you pick if nobody was watching, and why?',
    preferred:
      'Which way to sign on feels like the app’s preferred path? What makes you think that (size, position, wording, what you just watched)?',
    unclear: 'Is there an option you do not understand? Which one, and what do you think it might do?',
    chosen: 'Pick one and carry on. Which did you pick?',
    asksLead:
      'As you go, note every screen that asks you for something (an email, a code, a permission, an age, anything else).',
    asked: 'What it asked',
    thought: 'What I thought it was for',
    hesitate: 'Did I hesitate?',
    clearer: 'What would have made it clearer?',
  },
  firstLook: {
    title: '3. You are in. First look.',
    instruction:
      'When you land on the main screen, do not tap for ten seconds. If something pops up or speaks, read or listen to it first, then answer.',
    describe: 'Describe the screen as if the person reading this cannot see it.',
    what: 'What do you think you are looking at?',
    popup: 'If something appeared over the screen, or the phone spoke, what did you think it wanted you to do? Did you trust it?',
    tap: 'What would you tap first, and what do you expect to happen?',
    next: 'Where does the app seem to want you to go next? What on the screen makes you think that?',
  },
  hear: {
    title: '4. Try to hear something',
    instruction:
      'Do not ask how. Your only aim is to hear a piece of audio that belongs to a place. Write as you go, not afterwards.',
    tried: 'What I tried',
    happened: 'What happened',
    nextTry: 'What I thought I should do next',
    lost: 'At what moment did you feel lost?',
    helped: 'What did the app do that helped you?',
    hindered: 'What did the app do that got in the way?',
    obvious: 'If you heard something: was it obvious that you had found it, or did it just start? What told you it had worked?',
    stopped: 'If you could not hear anything: what stopped you? What would you have needed?',
  },
  after: {
    title: '5. After you have heard something, or given up',
    guess: 'Where would you go to hear it again? Write your guess first, then try.',
    tried: 'What actually happened when you tried?',
    else: 'What else do you think you can do now that you could not do when you first opened the app?',
  },
  explore: {
    title: '6. Guess, then look',
    instruction: 'Before you open anything new, fill in “I thought it was for”. Then open it and fill in the rest.',
    thought: 'I thought it was for',
    actual: 'What it actually is',
    clear: 'Clear or confusing?',
    tabs: 'The buttons along the bottom',
    menu: 'Any menu (an icon made of lines, or a list)',
    again: 'A way back to the audio I already heard',
    settings: 'Settings, if I can find them',
    other: 'One other thing I noticed',
    labels: 'Did the labels match what was inside? Note any word that meant something different from what you expected.',
  },
  make: {
    title: '7. If the app invites you to make something',
    instruction:
      'You do not have to record. If you do try, stop before you publish anything that other people could find, and write down what the app asked you to decide.',
    noticed: 'When did you first notice that you could add your own audio? What made you notice?',
    expect: 'What do you think would happen if you did?',
    feeling: 'Do you feel invited, or blocked? What gives you that feeling?',
    decisions: 'If you started a recording, what decisions did the app ask you to make, and which of them were unclear?',
  },
  curiosity: {
    title: '8. Five minutes of curiosity',
    instruction: 'Tap whatever you are curious about. Do not try to be systematic. For each new screen, one line is enough.',
    expected: 'What I expected',
    found: 'What I found',
    change: 'One thing I would change',
    left: 'Was there a moment you left a screen because you did not know what it was for? Which screen, and what would have kept you there?',
  },
  verdict: {
    title: '9. Your verdict',
    confusedLead: 'Three moments you were confused. For each: what you saw, what you thought it meant, and what you needed instead.',
    saw: 'What I saw',
    meant: 'What I thought it meant',
    needed: 'What I needed instead',
    workedLead: 'Three moments the app led you well. What did it do, and why did that work?',
    did: 'What the app did',
    why: 'Why that worked',
    change: 'If you could change one thing before the next person opens the app, what would it be, and why?',
    who: 'Who do you think this app is for?',
    again: 'Would you open it again tomorrow, without being asked? Why, or why not?',
    friend: 'Finish this sentence in your own words, as if you were telling a friend: OVERHEAR is an app that…',
  },
  lead: {
    title: 'Session notes',
    subtitle: 'For the person running the work experience session.',
    warning:
      'Do not share this page with students, and do not read it to them. The worksheet is about their first instinct, not whether they matched this path.',
    setupTitle: 'How to run it',
    setupPhone:
      'Give each student the worksheet on their own browser. One student per phone if you can. The password is first-instincts.',
    responses:
      'Submitted sheets are saved with the other feedback forms, under the title OVERHEAR first instincts.',
    setupFresh:
      'Use a phone that has never completed OVERHEAR’s first open, or delete and reinstall so they see the video and then the App Mode screen. If the phone has already been through it, they will skip the video and the first two sections will not match what they see.',
    setupWalk:
      'They need to be somewhere they can walk a short distance. The app is about audio left in real places. Do not demonstrate first.',
    pathTitle: 'What a first open is designed to do',
    pathIntro: 'Use this only afterwards, when you compare their notes with the path the app intends.',
    pathIntroTitle: 'The video',
    pathIntroBody:
      'The first thing they see is a video. It shows the idea of moving, collecting, listening and pausing. Words they may meet: pins, the map, Library, a welcome pin, recording, projects, parental controls.',
    pathEntryTitle: 'App Mode',
    pathEntryBody:
      'When the video ends they land on the App Mode screen and choose how to sign on. The options are Email Sign Up, Phone Sign Up, Continue as Guest, and Scan to join, plus Sign in with Google and Sign in with Apple. Continue as Guest is a valid way through. Scan to join is for an organisation or workshop code, which they will not have.',
    pathPermissionsTitle: 'Permissions',
    pathPermissionsBody:
      'The app asks for location so it can show them on the map and collect recordings. On Android it also explains that location can be used when the app is closed, for placing recordings. An age band may be asked so recordings with mature themes can be limited.',
    pathMapTitle: 'The map',
    pathMapBody:
      'A welcome note may say a welcome pin has been placed a short distance away, and invite them to walk to it and listen. A recording is collected when they are close enough to its pin. Library is where collected audio lives. Adding their own recording is meant to unlock after they have collected one. The bottom bar usually shows Map, Library, Record, Wander and Projects. A menu of lines opens the list of projects.',
    listenTitle: 'What to listen for when you read the sheets',
    listenHelp: 'The step where they first asked for help, and what they had already tried.',
    listenWords:
      'Words they used that the app does not use, and words the app uses that they could not explain: pin, collect, wander, project.',
    listenLead: 'Whether they thought the video, the welcome note, or the map was telling them what to do.',
    listenChoices: 'Whether guest, sign-up and scan to join felt like equal choices or one obvious path.',
    listenLocation: 'Whether the location permission felt explained.',
    listenMove: 'Whether they understood that they had to move, physically, before anything would play.',
    listenLibrary: 'Whether Library was where they expected to find audio again.',
    listenRecord: 'Whether recording felt invited or locked, and whether they knew why.',
    gap: 'The useful finding is the gap between this path and what they wrote.',
  },
}
